// /metodika — Blok 4 (2026-07-20, audit-artefakt "rýchle víťazstvá") — jedno
// miesto so všetkými pojmami, ktoré appka používa (tooltip-y na SearchPage,
// profil firmy, riziko), inšpirované Valida.sk "Metodika" stránkou z prieskumu
// konkurencie. Čisto statický obsah, žiadne API volanie — definície odrážajú
// presne to, čo appka reálne počíta (packages/shared/src/financial-ratios.ts,
// modules/risk/risk-signals.service.ts, docs/06-riziko-signaly.md), nič
// nevymyslené navyše.
//
// Lepivý obsah (Stitch redizajn 2026-07-20) — dlhá jednostĺpcová stránka bola
// ťažko prehľadateľná, `.section-rail` opakuje vzor z profilu firmy (bez
// rizikových bodiek, ktoré tu nedávajú zmysel).
import { useEffect, useState } from 'react';

function useScrollSpy(keys: string[]): string | null {
  const [active, setActive] = useState<string | null>(null);
  useEffect(() => {
    if (keys.length === 0) return;
    const visible = new Set<string>();
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) visible.add(entry.target.id);
          else visible.delete(entry.target.id);
        }
        const first = keys.find((k) => visible.has(k));
        if (first) setActive(first);
      },
      { rootMargin: '-12% 0px -70% 0px', threshold: 0 },
    );
    for (const key of keys) {
      const el = document.getElementById(key);
      if (el) observer.observe(el);
    }
    return () => observer.disconnect();
  }, [keys.join('|')]);
  return active;
}

interface Term {
  label: string;
  def: string;
  hint?: string;
}
interface Category {
  key: string;
  label: string;
  terms: Term[];
}

const CATEGORIES: Category[] = [
  {
    key: 'marze',
    label: 'Marže',
    terms: [
      {
        label: 'Hrubá marža (obchodná)',
        def: 'Aká časť tržieb z predaja tovaru ostane firme po odpočítaní nákladov na jeho obstaranie.',
        hint: 'Zmysluplné len pre firmy s tržbami z predaja tovaru — pri výrobných či servisných firmách (napr. IT) vyjde skreslené alebo prázdne.',
      },
      {
        label: 'EBITDA marža',
        def: 'Podiel prevádzkového zisku pred odpismi, úrokmi a daňami na tržbách — základná prevádzková ziskovosť bez vplyvu investícií a spôsobu financovania.',
      },
      {
        label: 'Prevádzková marža (EBIT)',
        def: 'Podiel prevádzkového zisku (už po odpisoch) na tržbách.',
      },
      {
        label: 'Čistá zisková marža',
        def: 'Koľko z každého eura tržieb ostane firme ako čistý zisk po zdanení.',
      },
    ],
  },
  {
    key: 'zadlzenost',
    label: 'Ukazovatele zadlženosti',
    terms: [
      {
        label: 'Celková zadlženosť',
        def: 'Aký podiel majetku firmy je financovaný cudzími zdrojmi (záväzkami) namiesto vlastného imania.',
      },
      {
        label: 'Zadlženosť vlastného imania (D/E)',
        def: 'Koľko eur záväzkov pripadá na jedno euro vlastného imania.',
      },
      {
        label: 'Krátkodobá zadlženosť',
        def: 'Aký podiel majetku je financovaný krátkodobými (do 1 roka splatnými) záväzkami.',
      },
      {
        label: 'Miera samofinancovania',
        def: 'Opak celkovej zadlženosti — akú časť majetku firma financuje z vlastných zdrojov.',
      },
      {
        label: 'Dlh/EBITDA',
        def: 'Koľko rokov prevádzkového zisku (pred odpismi) by firme trvalo splatiť všetky svoje záväzky.',
        hint: 'Aproximácia — appka nemá oddelený "úročený dlh" (len bankové úvery), preto počíta z celkových záväzkov. Vyjde preto systematicky vyššie číslo, než klasické "Dlh/EBITDA".',
      },
      {
        label: 'Úrokové krytie',
        def: 'Koľkokrát prevádzkový zisk pokrýva nákladové úroky. Nízka hodnota signalizuje problém splácať úroky z bežnej prevádzky.',
      },
    ],
  },
  {
    key: 'likvidita',
    label: 'Ukazovatele likvidity',
    terms: [
      {
        label: 'Bežná likvidita (3. stupňa)',
        def: 'Pomer celého obežného majetku ku krátkodobým záväzkom — schopnosť firmy splatiť krátkodobé záväzky zo všetkého, čo má rýchlo k dispozícii.',
      },
      {
        label: 'Rýchla likvidita (2. stupňa)',
        def: 'To isté ako bežná likvidita, ale bez zásob — tie sa na hotovosť premieňajú najpomalšie a najneisto.',
      },
      {
        label: 'Hotovostná likvidita (1. stupňa)',
        def: 'Pomer len skutočne likvidného majetku (hotovosť, bankové účty) ku krátkodobým záväzkom. Najprísnejšia a najkonzervatívnejšia miera.',
      },
    ],
  },
  {
    key: 'rentabilita',
    label: 'Ukazovatele rentability',
    terms: [
      {
        label: 'Návratnosť aktív (ROA)',
        def: 'Koľko čistého zisku firma vygenerovala na euro celkových aktív — ako efektívne firma zhodnocuje celý svoj majetok.',
      },
      {
        label: 'Návratnosť vlastného imania (ROE)',
        def: 'Koľko čistého zisku firma vygenerovala na euro vlastného imania — dôležité najmä pre vlastníkov/investorov.',
      },
    ],
  },
  {
    key: 'aktivita',
    label: 'Ukazovatele aktivity',
    terms: [
      {
        label: 'Obrat aktív',
        def: 'Koľkokrát ročne firma "obráti" svoj majetok cez tržby — vyššie číslo znamená efektívnejšie využitie majetku.',
      },
      {
        label: 'Obrat zásob / Doba obratu zásob',
        def: 'Ako rýchlo firma predá a doplní svoje zásoby — obrat je počet cyklov za rok, doba obratu je priemerný počet dní na jeden cyklus.',
        hint: 'Zmysluplné len pre firmy s tržbami z predaja tovaru (rovnaké obmedzenie ako pri hrubej marži).',
      },
      {
        label: 'Doba inkasa pohľadávok (DSO)',
        def: 'Priemerný počet dní, kým firme zaplatia jej odberatelia po vystavení faktúry.',
      },
      {
        label: 'Doba splácania záväzkov (DPO)',
        def: 'Priemerný počet dní, za ktoré firma platí svojim dodávateľom.',
        hint: 'Zmysluplné len pre firmy s tržbami z predaja tovaru (rovnaké obmedzenie ako pri hrubej marži).',
      },
    ],
  },
];

const RISK_SIGNALS: { label: string; points: string; source: string }[] = [
  {
    label: 'Diskvalifikovaná osoba vo vedení',
    points: '+40 / osobu',
    source: 'Register diskvalifikovaných osôb',
  },
  { label: 'Konkurz / reštrukturalizácia / likvidácia', points: '+45', source: 'Obchodný vestník' },
  { label: 'Záporné vlastné imanie', points: '+35', source: 'Register účtovných závierok' },
  {
    label: 'Altman Z" v rizikovej zóne (< 1,1)',
    points: '+30',
    source: 'Vypočítané z účtovnej závierky',
  },
  {
    label: 'Nedobrovoľný výmaz z RPVS',
    points: '+30',
    source: 'Register partnerov verejného sektora',
  },
  { label: 'Sankcia v RPVS', points: '+25', source: 'Register partnerov verejného sektora' },
  { label: 'Dlžník VšZP', points: '+20', source: 'Register dlžníkov VšZP' },
  {
    label: 'Dlžník Sociálnej poisťovne',
    points: '+20',
    source: 'Register dlžníkov Sociálnej poisťovne',
  },
  {
    label: 'Výrazný medziročný pokles vlastného imania (> 30 %)',
    points: '+20',
    source: 'Register účtovných závierok',
  },
  {
    label: 'Časté zmeny štatutárov (≥ 2 za 24 mesiacov)',
    points: '+15',
    source: 'História Registra právnických osôb',
  },
  {
    label: 'Mladá firma (< 2 roky od založenia)',
    points: '+10',
    source: 'Register právnických osôb',
  },
];

const TOC_ITEMS: { key: string; label: string }[] = [
  { key: 'riziko', label: 'Rizikové skóre' },
  { key: 'altman', label: 'Altman Z" skóre' },
  { key: 'dane', label: 'Index daň. spoľahlivosti' },
  ...CATEGORIES.map((c) => ({ key: c.key, label: c.label })),
  { key: 'kredit', label: 'Kreditné modely' },
];

export function MetodikaPage() {
  const active = useScrollSpy(TOC_ITEMS.map((i) => i.key));
  return (
    <main className="page page-with-toc">
      <h1 style={{ fontSize: '1.4rem' }}>Ako čítať dáta o firmách</h1>
      <p
        style={{
          color: 'var(--slate)',
          fontSize: '0.9rem',
          maxWidth: '62ch',
          marginBottom: '2rem',
        }}
      >
        Vysvetlenie všetkých finančných ukazovateľov, rizikového skóre a modelov, ktoré Corpuls
        počíta a zobrazuje na profile firmy. Definície tu presne zodpovedajú tomu, čo appka skutočne
        počíta — nič nie je zjednodušené na úkor presnosti.
      </p>

      <div className="doc-toc-layout">
        <nav className="section-rail" aria-label="Obsah stránky">
          <div className="rail-label">Obsah</div>
          {TOC_ITEMS.map((item) => (
            <a key={item.key} href={`#${item.key}`} className={active === item.key ? 'on' : ''}>
              {item.label}
            </a>
          ))}
        </nav>

        <div>
          <h2 id="riziko" style={sectionH}>
            Rizikové skóre
          </h2>
          <p style={pText}>
            Rizikové skóre je súčet bodov za jednotlivé signály nižšie, capnutý na 100. Vyššie číslo
            znamená vyššie riziko. Nie je to predikcia ani právne záväzné hodnotenie — je to
            orientačná heuristika založená na verejne dostupných registrových údajoch.
          </p>
          <div style={{ display: 'flex', gap: '10px', margin: '10px 0 18px', flexWrap: 'wrap' }}>
            <span className="pill low">0–19 nízke riziko</span>
            <span className="pill mid">20–49 stredné riziko</span>
            <span className="pill high">50–100 vysoké riziko</span>
          </div>
          <div className="overflow-x">
            <table className="dense-table">
              <thead>
                <tr>
                  <th>Signál</th>
                  <th>Zdroj</th>
                  <th className="amt">Body</th>
                </tr>
              </thead>
              <tbody>
                {RISK_SIGNALS.map((s) => (
                  <tr key={s.label}>
                    <td>{s.label}</td>
                    <td>{s.source}</td>
                    <td className="amt num">{s.points}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p style={{ ...pText, fontSize: '0.82rem', marginTop: '10px' }}>
            Niektoré udalosti sa na profile zobrazujú informačne, ale zámerne <b>nedávajú body</b>:
            zníženie základného imania (bežná firemná operácia), dobrovoľný výmaz a kvalifikovaný
            podnet v RPVS (rutina, resp. nepotvrdené), a Altman Z" v sivej zóne 1,1–2,6 (príliš veľa
            firiem by tam spadlo, aby to malo vypovedaciu hodnotu).
          </p>

          <h2 id="altman" style={sectionH}>
            Altman Z" skóre
          </h2>
          <p style={pText}>
            Nezávislý akademický ukazovateľ rizika bankrotu, zobrazený na profile firmy popri
            rizikovom skóre — nie je súčasťou jeho výpočtu ako jeden signál, je to samostatný "druhý
            názor". Verzia pre súkromné/neobchodované firmy (klasický Z-score z roku 1968 používa
            trhovú hodnotu vlastného imania, ktorú slovenské s.r.o./a.s. nemajú).
          </p>
          <pre style={formulaBox}>
            {`Z" = 6,56·X1 + 3,26·X2 + 6,72·X3 + 1,05·X4

X1 = (obežný majetok − krátkodobé záväzky) / aktíva spolu
X2 = nerozdelený zisk minulých rokov / aktíva spolu
X3 = výsledok hospodárenia z prevádzkovej činnosti / aktíva spolu
X4 = vlastné imanie (účtovná hodnota) / záväzky spolu`}
          </pre>
          <div style={{ display: 'flex', gap: '10px', margin: '10px 0 18px', flexWrap: 'wrap' }}>
            <span className="pill low">Z" &gt; 2,6 bezpečná zóna</span>
            <span className="pill mid">1,1–2,6 sivá zóna</span>
            <span className="pill high">Z" &lt; 1,1 riziková zóna</span>
          </div>
          <p style={{ ...pText, fontSize: '0.82rem' }}>
            Zdroj: Altman, E.I., Hartzell, J., Peck, M. (1995), "Emerging Markets Corporate Bonds: A
            Scoring System".
          </p>

          <h2 id="dane" style={sectionH}>
            Index daňovej spoľahlivosti
          </h2>
          <p style={pText}>
            Hodnotenie Finančnej správy SR, ktoré ukazuje, ako poctivo firma plní svoje daňové
            povinnosti (včasnosť podávania priznaní, platenia daní, súčinnosť s kontrolami). Škála:{' '}
            <b>vysoko spoľahlivý</b>, <b>spoľahlivý</b>, <b>menej spoľahlivý</b>. Nie je súčasťou
            nášho rizikového skóre — zobrazuje sa samostatne ako oficiálne hodnotenie štátu.
          </p>

          {CATEGORIES.map((cat) => (
            <div key={cat.key} id={cat.key}>
              <h2 style={sectionH}>{cat.label}</h2>
              <div className="overflow-x">
                <table className="dense-table">
                  <tbody>
                    {cat.terms.map((t) => (
                      <tr key={t.label}>
                        <td style={{ width: '30%', fontWeight: 600 }}>{t.label}</td>
                        <td>
                          {t.def}
                          {t.hint && (
                            <div
                              style={{
                                color: 'var(--slate)',
                                fontSize: '0.8rem',
                                marginTop: '4px',
                              }}
                            >
                              {t.hint}
                            </div>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ))}

          <h2 id="kredit" style={sectionH}>
            Kreditné modely — zložky Altman Z"
          </h2>
          <p style={pText}>
            X1–X4 sú štyri zložky použité vo vzorci Altman Z" vyššie, zobrazené aj samostatne v
            tabuľke ukazovateľov na profile firmy, aby bolo vidno, ktorá zložka najviac ťahá
            výsledné skóre nahor alebo nadol.
          </p>

          <p style={{ ...pText, fontSize: '0.82rem', marginTop: '2.5rem' }}>
            Všetky ukazovatele sú počítané z verejne dostupných účtovných závierok (Register
            účtovných závierok, MF SR, licencia CC0). Dáta majú informatívny charakter a nie sú
            právne záväzné — viď <a href="/podmienky">Podmienky používania</a>.
          </p>
        </div>
      </div>
    </main>
  );
}

const sectionH = { fontSize: '1.1rem', marginTop: '2rem', color: 'var(--ink)' } as const;
const pText = {
  color: 'var(--ink)',
  fontSize: '0.92rem',
  lineHeight: 1.7,
  maxWidth: '68ch',
} as const;
const formulaBox = {
  background: 'var(--bg-soft)',
  border: '1px solid var(--line)',
  borderRadius: '6px',
  padding: '14px 18px',
  fontFamily: 'var(--mono)',
  fontSize: '0.84rem',
  lineHeight: 1.7,
  whiteSpace: 'pre-wrap',
  overflowX: 'auto',
} as const;
